import React, { ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  BrFilter,
  ProductFilterInput,
  SearchProductsSortFragment,
} from '../../../../generated/product-service-graphql';
import './bloomreachFilters.scss';
import { useSearchParamHelpers } from '../../_shared/react-hooks';
import Checkbox from '../../_shared/ui-components/YogaUI/Checkbox';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import RadioButtons from 'src/scripts/templates/_shared/ui-components/YogaUI/RadioButtons';
import { PlpContext } from '../PLPContext';
import { FilterOption } from '../../search-page/types';
import { MAX_FILTER_ITEMS_SHOWN } from '../../search-page/SearchPageUtils/constants';
import { brFiltersToFilterOptions } from '../../search-page/SearchPageUtils/BrFiltersToFilterOptions';
import { SORT_BY_KEY } from './sorting-utils';

const sidebarBodySelector = '.filter-drawer-container .alo-sidebar-body';
const sidebarHeaderSelector = '.filter-drawer-container .alo-sidebar-header';

const BloomreachFilters = ({
  filters,
  availableFilters,
  selectedFilters,
  onChange,
  showSortOptions,
  sortOptions,
  disableShowMore = false,
  showSelectedFilterLabels = false,
}: {
  filters: BrFilter[];
  availableFilters: FilterOption[];
  selectedFilters: ProductFilterInput[];
  onChange: (filters: ProductFilterInput) => void;
  showSortOptions: boolean;
  sortOptions: SearchProductsSortFragment[];
  disableShowMore?: boolean;
  showSelectedFilterLabels?: boolean;
}) => {
  const { isPlpFiltersDrawerOpen } = useContext(PlpContext);
  const [expandedFilters, setExpandedFilters] = useState<{ [filterId: string]: boolean }>({});
  const [expandedGroups, setExpandedGroups] = useState<{ [filterId: string]: { [groupId: string]: boolean } }>({});

  const handleShowMoreClick = (filterId: string, groupId?: string) => {
    if (groupId) {
      setExpandedGroups((prev) => ({
        ...prev,
        [filterId]: {
          ...prev[filterId],
          [groupId]: !prev[filterId]?.[groupId],
        },
      }));
    } else {
      setExpandedFilters((prev) => ({
        ...prev,
        [filterId]: !prev[filterId],
      }));
    }
  };

  const isFilterExpanded = useMemo(() => (filterId: string) => expandedFilters[filterId] || false, [expandedFilters]);
  const isGroupExpanded = useMemo(
    () => (filterId: string, groupId: string) => expandedGroups[filterId]?.[groupId] || false,
    [expandedGroups]
  );

  const onOptionChange = (filter: BrFilter, value: string, checked: boolean) => {
    const input = selectedFilters.find((selectedFilter) => selectedFilter.key === filter.id);

    if (input) {
      if (checked) {
        onChange({ key: filter.id, values: [...input.values, value] });
      } else {
        onChange({ key: filter.id, values: input.values.filter((v) => v !== value) });
      }
    } else {
      onChange({ key: filter.id, values: [value] });
    }
  };

  const isFilterAvailable = useMemo(() => {
    return (filterId: string, value: string) => {
      return availableFilters.some(
        (availableFilter) => availableFilter.filterId === filterId && availableFilter.filterValueId === value
      );
    };
  }, [availableFilters]);

  const isFilterValueSelected = useMemo(() => {
    return (filter: BrFilter, value: string) => {
      return selectedFilters.some(
        (selectedFilter) =>
          selectedFilter.key === filter.id &&
          selectedFilter.values.some((selectedFilterValue) => selectedFilterValue === value)
      );
    };
  }, [selectedFilters]);

  useEffect(() => {
    const scroller = document.querySelector(sidebarBodySelector);
    !isPlpFiltersDrawerOpen && scroller && (scroller.scrollTop = 0);
  }, [isPlpFiltersDrawerOpen]);

  // Reusable function for rendering `Show More vs Show Less`
  const renderShowMoreLink = (filterId: string, isExpanded: boolean, itemCount: number, groupId?: string) => {
    if (disableShowMore) return null;
    return itemCount > MAX_FILTER_ITEMS_SHOWN ? (
      <ShowMoreLink
        isExpanded={isExpanded}
        itemCount={itemCount}
        onClick={() => handleShowMoreClick(filterId, groupId)}
        groupId={groupId}
      />
    ) : null;
  };

  const sliceLength = (filterlength: number, isExpanded: boolean) => {
    return disableShowMore || isExpanded ? filterlength : MAX_FILTER_ITEMS_SHOWN;
  };

  const FilterOptionList: FilterOption[] = brFiltersToFilterOptions(filters || []);

  return (
    <div className="br-filter-content">
      {showSortOptions && <MobileSortOptions sortOptions={sortOptions} />}
      {filters.map((filter) => {
        const selectedFilterLabels = getSelectedFiltersLabels(filter, FilterOptionList, selectedFilters);
        const pendingFiltersCount = getSelectedFiltersCount(filter, selectedFilters);
        const labelWithCount =
          pendingFiltersCount > 0
            ? `${filter.label}${
                showSelectedFilterLabels
                  ? `<div class="applied-filter-labels">${selectedFilterLabels.join(', ')}</div>`
                  : ` (${pendingFiltersCount})`
              }`
            : filter.label;
        const isExpanded = isFilterExpanded(filter.id);
        if (filter.__typename === 'ColorFilter') {
          return (
            <ListFilter
              useAccordion={true}
              id={filter.id}
              label={labelWithCount}
              children={
                <>
                  <div className="checkboxes">
                    {filter.values.slice(0, sliceLength(filter.values.length, isExpanded)).map((value) => (
                      <ListFilterCheckbox
                        key={value.id}
                        id={value.id}
                        label={value.label}
                        swatchColor={value.swatch}
                        isChecked={isFilterValueSelected(filter, value.id)}
                        disabled={!isFilterAvailable(filter.id, value.id)}
                        onChange={(checked: boolean) => onOptionChange(filter, value.id, checked)}
                      />
                    ))}
                  </div>
                  {renderShowMoreLink(filter.id, isExpanded, filter.values.length)}
                </>
              }
            />
          );
        } else if (filter.__typename === 'FabricFilter') {
          return (
            <ListFilter
              useAccordion={true}
              id={filter.id}
              label={labelWithCount}
              children={
                <>
                  <div className="checkboxes">
                    {filter.values.slice(0, sliceLength(filter.values.length, isExpanded)).map((value) => (
                      <ListFilterCheckbox
                        key={value.id}
                        id={value.id}
                        label={value.label}
                        description={value.description}
                        isChecked={isFilterValueSelected(filter, value.id)}
                        disabled={!isFilterAvailable(filter.id, value.id)}
                        onChange={(checked: boolean) => onOptionChange(filter, value.id, checked)}
                      />
                    ))}
                  </div>
                  {renderShowMoreLink(filter.id, isExpanded, filter.values.length)}
                </>
              }
            />
          );
        } else if (filter.__typename === 'ListFilter') {
          return (
            <ListFilter
              useAccordion={true}
              id={filter.id}
              label={labelWithCount}
              children={
                <>
                  <div className="checkboxes">
                    {filter.values.slice(0, sliceLength(filter.values.length, isExpanded)).map((value) => (
                      <ListFilterCheckbox
                        key={value.id}
                        id={value.id}
                        label={value.label}
                        isChecked={isFilterValueSelected(filter, value.id)}
                        disabled={!isFilterAvailable(filter.id, value.id)}
                        onChange={(checked: boolean) => onOptionChange(filter, value.id, checked)}
                      />
                    ))}
                  </div>
                  {renderShowMoreLink(filter.id, isExpanded, filter.values.length)}
                </>
              }
            />
          );
        } else if (filter.__typename === 'GroupedListFilter') {
          return (
            <ListFilter
              useAccordion={true}
              id={filter.id}
              label={labelWithCount}
              children={filter.groups.map((group) => {
                const groupId = group.label;
                return (
                  <ListFilter
                    id={group.label}
                    label={group.label}
                    isSubgroup={true}
                    children={
                      <>
                        <div className="checkboxes">
                          {group.values
                            .slice(0, sliceLength(group.values.length, isGroupExpanded(filter.id, groupId)))
                            .map((value) => (
                              <ListFilterCheckbox
                                key={value.id}
                                id={value.id}
                                label={value.label}
                                isChecked={isFilterValueSelected(filter, value.id)}
                                disabled={!isFilterAvailable(filter.id, value.id)}
                                onChange={(checked: boolean) => onOptionChange(filter, value.id, checked)}
                              />
                            ))}
                        </div>
                        {renderShowMoreLink(
                          filter.id,
                          isGroupExpanded(filter.id, groupId),
                          group.values.length,
                          groupId
                        )}
                      </>
                    }
                  />
                );
              })}
            />
          );
        } else if (filter.__typename === 'SizeFilter') {
          return (
            <ListFilter
              useAccordion={true}
              id={filter.id}
              label={labelWithCount}
              children={filter.groups.map((group) => {
                const groupId = group.label;
                return (
                  <ListFilter
                    id={group.label}
                    label={group.label}
                    isSubgroup={true}
                    children={
                      <>
                        <ul className="size-buttons">
                          {group.values
                            .slice(0, sliceLength(group.values.length, isGroupExpanded(filter.id, groupId)))
                            .map((value) => (
                              <SizeOption
                                key={value.id}
                                label={value.label}
                                isChecked={isFilterValueSelected(filter, value.id)}
                                onChange={(checked: boolean) => onOptionChange(filter, value.id, checked)}
                              />
                            ))}
                        </ul>
                        {renderShowMoreLink(
                          filter.id,
                          isGroupExpanded(filter.id, groupId),
                          group.values.length,
                          groupId
                        )}
                      </>
                    }
                  />
                );
              })}
            />
          );
        } else if (filter.__typename === 'NumericSliderFilter') {
          return null;
        } else return null;
      })}
    </div>
  );
};

const ShowMoreLink = ({
  isExpanded,
  itemCount,
  onClick,
  groupId,
}: {
  isExpanded: boolean;
  itemCount: number;
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  groupId?: string;
}) => {
  return itemCount > MAX_FILTER_ITEMS_SHOWN ? (
    <div className={groupId ? 'show-more-container' : ''}>
      <button className="show-more-link" onClick={onClick} aria-expanded={isExpanded}>
        {isExpanded ? 'Show Less' : `Show ${itemCount - MAX_FILTER_ITEMS_SHOWN} More`}
      </button>
    </div>
  ) : null;
};

const ListFilter = ({
  id,
  label,
  isSubgroup = false,
  children,
  useAccordion = false,
}: {
  id: string;
  label: string;
  isSubgroup?: boolean;
  children: ReactNode;
  useAccordion?: boolean;
}) => {
  const Title = ({ label, isSubgroup }: { label: string; isSubgroup?: boolean }) => {
    if (isSubgroup) {
      return <p className="body-small semibold">{label}</p>;
    } else {
      return (
        <p
          className="accordion-title body uppercase"
          dangerouslySetInnerHTML={{
            __html: label,
          }}
        ></p>
      );
    }
  };
  if (useAccordion) {
    return (
      <FilterAccordionBlock id={id} key={id}>
        <AccordionSummary
          classes={{ root: 'accordion-summary', content: 'accordion-summary-content' }}
          expandIcon={<ExpandMore />}
        >
          <Title label={label} isSubgroup={isSubgroup} />
        </AccordionSummary>
        <AccordionDetails className="accordion-details">{children}</AccordionDetails>
      </FilterAccordionBlock>
    );
  } else {
    return (
      <div key={id} className="filter-groups d-flex flex-column">
        <Title label={label} isSubgroup={isSubgroup} />
        {children}
      </div>
    );
  }
};

const ListFilterCheckbox = ({
  id,
  label,
  description,
  swatchColor,
  isChecked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  description?: string;
  swatchColor?: string;
  isChecked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) => {
  return (
    <div className="filter-checkbox">
      <Checkbox
        key={id}
        options={{
          id: swatchColor ? `color-${label.toLowerCase()}` : id,
          label: swatchColor ? (
            <label className="color-option-label" htmlFor={`color-${label.toLowerCase()}`}>
              <span
                className="filter-bubble swatch"
                swatch-name={label}
                style={{
                  backgroundColor: swatchColor,
                }}
              />{' '}
              {label}
            </label>
          ) : (
            label
          ),
          name: `${description}-${label}`,
          value: label,
          checked: isChecked,
          disabled: disabled,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.checked),
        }}
      />
      {description && <div className={`label-description ${disabled ? 'disabled' : ''}`}>{description}</div>}
    </div>
  );
};

const MobileSortOptions = ({ sortOptions }: { sortOptions: SearchProductsSortFragment[] }) => {
  const options = sortOptions.map(({ label, value }) => [label, value]);
  const { setSearchParams, searchParams } = useSearchParamHelpers(window.location.search, { replace: true });
  const sortParam = searchParams.get(SORT_BY_KEY) ?? undefined;
  const setSortByValue = (sortByValue: string) => {
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set(SORT_BY_KEY, sortByValue);
    setSearchParams(currentParams);
  };
  return (
    <FilterAccordionBlock id={'sort'}>
      <AccordionSummary
        classes={{ root: 'accordion-summary', content: 'accordion-summary-content' }}
        expandIcon={<ExpandMore />}
      >
        <p className="accordion-title body uppercase">Sort By</p>
      </AccordionSummary>
      <AccordionDetails className="accordion-details">
        <RadioButtons
          options={{
            labelValuePairs: options,
            radioRowClasses: 'searchpage-sortby-row',
            defaultSelected: true,
            defaultValue: sortParam,
          }}
          setRadioValue={setSortByValue}
        />
      </AccordionDetails>
    </FilterAccordionBlock>
  );
};

const SizeOption = ({
  label,
  isChecked,
  onChange,
}: {
  label: string;
  isChecked: boolean;
  onChange: (checked: boolean) => void;
}) => {
  return (
    <button className={`size-buttons__button ${isChecked ? 'active' : ''}`} onClick={(event) => onChange(!isChecked)}>
      {label}
    </button>
  );
};

const FilterAccordionBlock = ({ id, children }: { children: ReactNode; id: string }) => {
  const filterItemBlockRef = useRef<HTMLDivElement>(null);
  const { isPlpFiltersDrawerOpen, activeFilterAccordion, setActiveFilterAccordion } = useContext(PlpContext);
  const [expanded, setExpanded] = React.useState(false);
  const filterItemBlock = filterItemBlockRef?.current;

  const handleChange = () => (event: React.ChangeEvent<{}>, expanded: boolean) => {
    setExpanded(expanded);
    activeFilterAccordion === id && setActiveFilterAccordion('');
  };

  useEffect(() => {
    isPlpFiltersDrawerOpen ? setExpanded(activeFilterAccordion === id) : setExpanded(false);
  }, [isPlpFiltersDrawerOpen, activeFilterAccordion, id]);

  useEffect(() => {
    if (expanded && activeFilterAccordion === id && filterItemBlock) {
      const scroller = document.querySelector(sidebarBodySelector);
      if (scroller) {
        const filterHeader = document.querySelector(sidebarHeaderSelector);
        const { height: filterHeaderHeight = 0 } = filterHeader?.getBoundingClientRect() || {};
        const { top: filterItemBlockTop = 0 } = filterItemBlock?.getBoundingClientRect();
        scroller.scrollTop = filterItemBlockTop - filterHeaderHeight + scroller.scrollTop;
      }
    }
  }, [expanded, activeFilterAccordion, id, filterItemBlock]);

  return children ? (
    <Accordion
      classes={{ root: 'filter-accordion', expanded: 'expanded' }}
      TransitionProps={activeFilterAccordion === id ? { timeout: 0 } : undefined}
      ref={filterItemBlockRef}
      expanded={expanded}
      onChange={handleChange()}
    >
      {children}
    </Accordion>
  ) : null;
};

export const getSelectedFiltersCount = (filter: BrFilter, selectedFilters: ProductFilterInput[]) => {
  const selectedFilter = selectedFilters.find((selected) => filter.id === selected.key);
  return selectedFilter ? selectedFilter.values.length : 0;
};

const getSelectedFiltersLabels = (
  filter: BrFilter,
  FilterOptionList: FilterOption[],
  selectedFilters: ProductFilterInput[]
) => {
  const selectedFilter = selectedFilters.find((selected) => filter.id === selected.key);
  return selectedFilter
    ? selectedFilter.values.map(
        (value) =>
          FilterOptionList?.find((filterObj) => filterObj.filterValueInputValue === value)?.filterValueLabel || ''
      )
    : [];
};

export default BloomreachFilters;
